import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

export interface PluggyItemRow {
  id: string;
  pluggy_item_id: string;
  connector_name: string | null;
  status: string | null;
  last_synced_at: string | null;
  error_message: string | null;
  created_at: string;
}

async function invokePluggy<T = Record<string, unknown>>(
  fn: string,
  body: Record<string, unknown> = {},
): Promise<T & { ok: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    // extrai o corpo real do FunctionsHttpError
    let detail = error.message as string;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const b = await ctx.json();
        if (b?.error) detail = b.error;
      } catch { /* mantém mensagem genérica */ }
    }
    throw new Error(detail);
  }
  if (!data?.ok) throw new Error(data?.error ?? `Falha em ${fn}.`);
  return data;
}

/** Conexões bancárias (itens Pluggy) do tenant + ações da integração. */
export function usePluggy() {
  const { tenantId } = useTenant();
  const [items, setItems] = useState<PluggyItemRow[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    const { data } = await supabase
      .from('pluggy_items')
      .select('id, pluggy_item_id, connector_name, status, last_synced_at, error_message, created_at')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    setItems((data as unknown as PluggyItemRow[]) ?? []);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { void reload(); }, [reload]);

  /** Token efêmero para abrir o widget (item_id presente = reconectar). */
  const getConnectToken = useCallback(async (itemId?: string): Promise<string> => {
    const data = await invokePluggy<{ connect_token: string }>('pluggy-connect-token',
      itemId ? { item_id: itemId } : {});
    return data.connect_token;
  }, []);

  /** Registra o item após sucesso do widget e roda o backfill inicial. */
  const registerItem = useCallback(async (itemId: string) => {
    return invokePluggy('pluggy-item-callback', { item_id: itemId, tenant_id: tenantId });
  }, [tenantId]);

  /** Sincronização manual ("Sincronizar agora"). */
  const syncNow = useCallback(async () => {
    return invokePluggy('pluggy-sync', { tenant_id: tenantId });
  }, [tenantId]);

  /** Revoga a conexão (consentimento) — contas e histórico permanecem. */
  const disconnect = useCallback(async (itemId: string) => {
    return invokePluggy('pluggy-disconnect', { item_id: itemId });
  }, []);

  return { items, loading, reload, getConnectToken, registerItem, syncNow, disconnect };
}
